import * as React from 'react';
import { styled, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import CssBaseline from '@mui/material/CssBaseline';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Tooltip from '@mui/material/Tooltip';
import * as constants from '../misc/Constants.tsx';
import { useNavigate, useLocation } from 'react-router-dom';
import { DataManVocabPage } from './DataManVocabPageComp/DataManVocabPage.tsx';
import { DataManResistanceDataPage } from './DataManResistanceDataPage.tsx';
import { ResistanceDataPage } from './ResistanceDataPageComp/ResistanceDataPage.tsx';
import { DashboardPage } from './DashboardPageComp/DashboardPage.tsx';
import { UsersPage } from './UsersPageComp/UsersPage.tsx';
import * as user_functions from '../misc/UserFunctions.ts';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import { sanitize_id } from '../misc/CommonFunctions.ts';
import { Theme } from '@mui/material/styles';
import MuiDrawer, { DrawerProps } from '@mui/material/Drawer';
import { CSSObject } from '@mui/system';
import { FileUpload } from './FileUploadComp/Upload.tsx';
import { ProfilePage } from './ProfilePageComp/ProfilePage.tsx';

const statusCreateNew = 'createNew';
const drawerWidth = 220;

export default function MainPage() {
    const theme = useTheme();
    const [open, setOpen] = React.useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);

    // Derive the current page from the URL
    const currentPageUrl = location.pathname.substring(1) || constants.resistanceDataPage;
    const currentPage = constants.urlToPageName(currentPageUrl);

    const handlePageChange = (page) => {
        navigate('/' + constants.pageToUrlName[page]);
    };

    const handleDrawerOpen = () => {
        setOpen(true);
    };

    const handleDrawerClose = () => {
        setOpen(false);
    };

    const shouldShowPage = (page: string) => {
        const needsAdmin = constants.pageToNeedsAdmin[page];
        if (!needsAdmin) return true;

        return user_functions.isUserAdmin();
    }


    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />
            <AppBar
                position="fixed"

                open={open}>

                <Toolbar>

                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        onClick={handleDrawerOpen}
                        edge="start"
                        sx={{
                            marginRight: 5,
                            ...(open && { display: 'none' }),
                        }}
                    >
                        <MenuIcon />
                    </IconButton>
                    <Typography variant="h6" noWrap component="div">
                        <span id='header'>
                            Resistance DB - {currentPage} - {user_functions.getLoggedInUser()} -- ({user_functions.getRoleOrRedirect()}) - v1.07
                        </span>
                    </Typography>
                </Toolbar>
            </AppBar>
            <Drawer
                theme={theme}
                variant="permanent"
                open={open}>
                <DrawerHeader>
                    <IconButton onClick={handleDrawerClose}>
                        {theme.direction === 'rtl' ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                    </IconButton>
                </DrawerHeader>
                <Divider />
                <List>
                    {constants.pages.filter(page => shouldShowPage(page)).map((text, index) => (
                        <ListItem key={text} disablePadding sx={{ display: 'block' }}>
                            <ListItemButton
                                id={sanitize_id(text)}
                                onClick={() => { handlePageChange(text) }}
                                sx={{
                                    minHeight: 48,
                                    justifyContent: open ? 'initial' : 'center',
                                    px: 2.5,
                                }}
                            >
                                <ListItemIcon
                                    sx={{
                                        minWidth: 0,
                                        mr: open ? 3 : 'auto',
                                        justifyContent: 'center',
                                    }}
                                >
                                    {!open &&
                                        <Tooltip

                                            title={text}
                                            placement="right">
                                            {constants.pageToicon[text]}
                                        </Tooltip>
                                    }

                                    {open && constants.pageToicon[text]}


                                    {!open &&
                                        <Typography variant="caption" display="block"   >

                                        </Typography>
                                    }

                                </ListItemIcon>

                                {open &&
                                    <Typography variant="caption" display="block"   >
                                        {text}
                                    </Typography>

                                }
                            </ListItemButton>
                        </ListItem>
                    ))}
                </List>

            </Drawer>
            <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
                <DrawerHeader />

                {currentPage === constants.dashboardPage &&
                    <DashboardPage />
                }

                {currentPage === constants.resistanceDataPage &&
                    <ResistanceDataPage is_admin={user_functions.isUserAdmin()} show_export={false} />
                }

                {currentPage === constants.dataManVocabPage &&
                    <DataManVocabPage />
                }

                {currentPage === constants.dataManResDataPage &&
                    <DataManResistanceDataPage is_admin={user_functions.isUserAdmin()} />
                }

                {currentPage === constants.uploadPage &&
                    <FileUpload />
                }

                {currentPage === constants.userManagementPage &&
                    <UsersPage is_admin={user_functions.isUserAdmin()} />
                }

                {currentPage === constants.profilePage &&
                    <ProfilePage />
                }
            </Box>
        </Box>
    );
}


const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),

    ...theme.mixins.toolbar,
}));


const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    }),
}));


interface AppBarProps extends MuiAppBarProps {
    open?: boolean;
}

interface AppBarProps extends MuiAppBarProps {
    open?: boolean;
}


const closedMixin = (theme: Theme): CSSObject => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(8)} + 1px)`,
    },
});

const openedMixin = (theme: Theme): CSSObject => ({
    width: drawerWidth,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
});

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme, open }: { theme: Theme; open: boolean }) => ({
        width: drawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        ...(open && {
            ...openedMixin(theme),
            '& .MuiDrawer-paper': openedMixin(theme),
        }),
        ...(!open && {
            ...closedMixin(theme),
            '& .MuiDrawer-paper': closedMixin(theme),
        }),
    }),
);